import React from 'react';
import { ToxnotServiceClient } from '../../../../../store/ToxnotServiceClient';
import { useLocalizer } from '../../hooks/useLocalizer';

interface UseGapReportDeliveryOptions {
    requestId: string;
    operationId: string;
    defaultRecipientEmail: string;
    recipientEmailMaxLength?: number;
    composeModalId: string;
    onEmailSent: (recipientEmail: string) => Promise<void>;
}

interface UseGapReportDeliveryResult {
    recipientEmail: string;
    setRecipientEmail: React.Dispatch<React.SetStateAction<string>>;
    message: string;
    setMessage: React.Dispatch<React.SetStateAction<string>>;
    isSending: boolean;
    hasSent: boolean;
    hasDownloaded: boolean;
    composeErrorMessage: string | null;
    showComposeModal: () => void;
    downloadReport: () => void;
    sendReport: () => Promise<void>;
}

export const useGapReportDelivery = ({
    requestId,
    operationId,
    defaultRecipientEmail,
    recipientEmailMaxLength,
    composeModalId,
    onEmailSent
}: UseGapReportDeliveryOptions): UseGapReportDeliveryResult => {
    const localize = useLocalizer();
    const serviceClient = React.useMemo(() => new ToxnotServiceClient(), []);
    const [recipientEmail, setRecipientEmail] = React.useState(defaultRecipientEmail);
    const [message, setMessage] = React.useState('');
    const [isSending, setIsSending] = React.useState(false);
    const [hasSent, setHasSent] = React.useState(false);
    const [hasDownloaded, setHasDownloaded] = React.useState(false);
    const [composeErrorMessage, setComposeErrorMessage] = React.useState<string | null>(null);

    React.useEffect(() => {
        setRecipientEmail(defaultRecipientEmail);
        setHasSent(false);
        setHasDownloaded(false);
    }, [defaultRecipientEmail, operationId, requestId]);

    const showComposeModal = React.useCallback(() => {
        setComposeErrorMessage(null);
        // Static backdrop + no keyboard dismiss so the modal cannot be closed mid-send.
        $(`#${composeModalId}`).modal({ backdrop: 'static', keyboard: false });
    }, [composeModalId]);

    const hideComposeModal = React.useCallback(() => {
        $(`#${composeModalId}`).modal('hide');
    }, [composeModalId]);

    const downloadReport = React.useCallback(() => {
        setHasDownloaded(true);
        window.location.href = serviceClient.getDataCollectionGapReportDownloadUrl(requestId, operationId);
    }, [operationId, requestId, serviceClient]);

    const sendReport = React.useCallback(async (): Promise<void> => {
        const trimmedEmail = recipientEmail.trim();

        if (trimmedEmail.length === 0) {
            setComposeErrorMessage(localize('Please enter a valid email address.'));
            return;
        }

        if (recipientEmailMaxLength && trimmedEmail.length > recipientEmailMaxLength) {
            setComposeErrorMessage(localize(`Email addresses cannot exceed ${recipientEmailMaxLength} characters.`));
            return;
        }

        setIsSending(true);
        setComposeErrorMessage(null);

        try {
            const result = await serviceClient.sendDataCollectionGapReportEmail({
                requestId,
                operationId,
                recipientEmail: trimmedEmail,
                message
            });

            if (result.success) {
                setHasSent(true);
                setIsSending(false);
                hideComposeModal();
                await onEmailSent(trimmedEmail);
                return;
            }

            setComposeErrorMessage(result.resultMessage || localize('Sorry, there was a problem sending the gap report. Please try again.'));
        } catch {
            setComposeErrorMessage(localize('Sorry, there was a problem sending the gap report. Please try again.'));
        }

        setIsSending(false);
    }, [
        hideComposeModal,
        localize,
        message,
        onEmailSent,
        operationId,
        recipientEmail,
        recipientEmailMaxLength,
        requestId,
        serviceClient
    ]);

    return {
        recipientEmail,
        setRecipientEmail,
        message,
        setMessage,
        isSending,
        hasSent,
        hasDownloaded,
        composeErrorMessage,
        showComposeModal,
        downloadReport,
        sendReport
    };
};
